import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Search,
  Visibility,
  Store as StoreIcon,
  Add,
  LocationOn,
  Phone,
  Business,
  Code,
  LocalShipping,
  Settings,
  Schedule,
  CheckCircle,
  Edit,
  Refresh
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import api from '../../utils/api';
import GoogleMapComponent from '../../components/common/GoogleMap';
import { cacheEndpoints } from '../../utils/apiCache';

const StoreList = () => {
  const { actions } = useApp();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStore, setSelectedStore] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    storeName: '',
    location: '',
    branchId: '',
    locCode: '',
    address: '',
    pincode: '',
    contactNo: '',
    deliveryRadius: 5000,
    status: true,
    selfPickup: false,
    restrictCOD: false,
    isDarkStore: false,
    mainStoreId: ''
  });

  useEffect(() => {
    fetchStores();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStores = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const storesData = await cacheEndpoints.stores.getAll(forceRefresh);
      setStores(storesData);
    } catch (error) {
      actions.showSnackbar('Error fetching stores', 'error');
      console.error('Error fetching stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    // Clear cache and force refresh
    cacheEndpoints.stores.clear();
    await fetchStores(true);
    actions.showSnackbar('Stores refreshed successfully', 'success');
  };

  const handleViewStore = (store) => {
    setSelectedStore(store);
    setViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedStore(null);
  };

  const handleEditStore = (store) => {
    setSelectedStore(store);
    // Pre-fill form with store data
    setEditFormData({
      storeName: store.storeName || '',
      location: store.location || '',
      branchId: store.branchId || '',
      locCode: store.locCode || '',
      address: store.address || '',
      pincode: store.pincode || '',
      contactNo: store.contactNo || '',
      deliveryRadius: store.deliveryRadius || 5000,
      status: store.status !== undefined ? store.status : true,
      selfPickup: store.selfPickup || false,
      restrictCOD: store.restrictCOD || false,
      isDarkStore: store.isDarkStore || false,
      mainStoreId: store.mainStoreId || ''
    });
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedStore(null);
    setEditFormData({
      storeName: '',
      location: '',
      branchId: '',
      locCode: '',
      address: '',
      pincode: '',
      contactNo: '',
      deliveryRadius: 5000,
      status: true,
      selfPickup: false,
      restrictCOD: false,
      isDarkStore: false,
      mainStoreId: ''
    });
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEditMapClick = (lat, lng) => {
    setEditFormData(prev => ({
      ...prev,
      location: `${lat.toFixed(6)}, ${lng.toFixed(6)}`
    }));
    actions.showSnackbar('Location updated on map', 'success');
  };

  const validateEditForm = () => {
    const errors = [];
    
    if (!editFormData.storeName.trim()) errors.push('Store name is required');
    if (!editFormData.branchId.trim()) errors.push('Branch ID is required');
    if (!editFormData.location.trim()) errors.push('Location coordinates are required');
    if (!editFormData.locCode.trim()) errors.push('Location code is required');
    if (editFormData.isDarkStore && !editFormData.mainStoreId) {
      errors.push('Main store selection is required for dark stores');
    }
    if (!editFormData.address.trim()) errors.push('Address is required');
    if (!editFormData.pincode || editFormData.pincode.length !== 6) errors.push('Valid 6-digit pincode is required');
    if (!editFormData.contactNo || !/^[6-9]\d{9}$/.test(editFormData.contactNo)) {
      errors.push('Valid Indian mobile number is required');
    }
    if (!editFormData.deliveryRadius || editFormData.deliveryRadius <= 0) {
      errors.push('Delivery radius must be greater than 0');
    }

    return errors;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateEditForm();
    if (errors.length > 0) {
      actions.showSnackbar(errors[0], 'error');
      return;
    }

    try {
      await api.put(`/stores/${selectedStore.id}`, editFormData);
      actions.showSnackbar('Store updated successfully!', 'success');
      
      // Clear cache to ensure fresh data
      cacheEndpoints.stores.clear();
      
      handleCloseEditModal();
      fetchStores(); // Refresh the store list
    } catch (error) {
      actions.showSnackbar(error.response?.data?.message || 'Error updating store', 'error');
    }
  };

  const filteredStores = stores.filter(store =>
    store.storeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.branchId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.contactNo?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    // Handle both boolean and string status values
    if (typeof status === 'boolean') {
      return status ? 'success' : 'error';
    }
    // Legacy string support
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    // Handle both boolean and string status values
    if (typeof status === 'boolean') {
      return status ? 'Active' : 'Inactive';
    }
    // Legacy string support
    return status === 'active' ? 'Active' : 'Inactive';
  };

  const getStoreTypeColor = (isDarkStore) => {
    return isDarkStore ? 'secondary' : 'primary';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading stores...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <StoreIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Store Management
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleRefresh}
              startIcon={<Refresh />}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              component={Link}
              to="/super-admin/stores/add"
              variant="contained"
              startIcon={<Add />}
            >
              Add New Store
            </Button>
          </Box>
        </Box>

        {/* Search */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search stores by name, branch ID, or contact number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 500 }}
          />
        </Box>

        {/* Store Count */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Showing {filteredStores.length} of {stores.length} stores
        </Typography>

        {/* Stores Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Branch ID</strong></TableCell>
                <TableCell><strong>Store Name</strong></TableCell>
                                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Delivery Radius</strong></TableCell>
                <TableCell><strong>Self Pickup</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStores.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" component="div" color="text.secondary">
                      {searchTerm ? 'No stores found matching your search.' : 'No stores available.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredStores.map((store) => (
                  <TableRow key={store.id} hover>
                    <TableCell>
                      <Typography variant="body2" component="span" fontWeight="bold">
                        {store.branchId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" component="span">
                        {store.storeName}
                      </Typography>
                      {store.isDarkStore && store.mainStore && (
                        <Typography variant="caption" component="div" color="text.secondary" display="block">
                          Main: {store.mainStore.storeName}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={store.isDarkStore ? 'Dark Store' : 'Store'}
                        color={getStoreTypeColor(store.isDarkStore)}
                        size="small"
                      />
                                          </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(store.status)}
                        color={getStatusColor(store.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" component="span">
                        {store.deliveryRadius} m
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={store.selfPickup ? 'Yes' : 'No'}
                        color={store.selfPickup ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={() => handleViewStore(store)}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => handleEditStore(store)}
                          startIcon={<Edit />}
                        >
                          Edit
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Store Details Modal */}
      <Dialog 
        open={viewModalOpen} 
        onClose={handleCloseViewModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <StoreIcon color="primary" />
            <Typography variant="h5">
              {selectedStore?.storeName}
            </Typography>
            <Chip 
              label={selectedStore?.isDarkStore ? 'Dark Store' : 'Store'} 
              color={getStoreTypeColor(selectedStore?.isDarkStore)}
              size="small"
            />
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedStore && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Basic Information Card */}
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Business color="primary" />
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Branch ID
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {selectedStore.branchId}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Store Name
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {selectedStore.storeName}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Location Code
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                          {selectedStore.locCode || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Status
                        </Typography>
                        <Chip 
                          label={getStatusLabel(selectedStore.status)} 
                          color={getStatusColor(selectedStore.status)}
                          size="medium"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Dark Store Configuration */}
              {selectedStore.isDarkStore && selectedStore.mainStore && (
                <Card elevation={1}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Settings color="primary" />
                      Dark Store Configuration
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Main Store
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {selectedStore.mainStore.storeName}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Main Store Branch ID
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {selectedStore.mainStoreId}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Location Information */}
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn color="primary" />
                    Location Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Coordinates
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                          {selectedStore.location}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Pincode
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {selectedStore.pincode}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Address
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {selectedStore.address}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Contact & Delivery */}
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShipping color="primary" />
                    Contact & Delivery
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Contact Number
                        </Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone fontSize="small" />
                          {selectedStore.contactNo}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Delivery Radius
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {selectedStore.deliveryRadius} meters
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Store Features */}
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle color="primary" />
                    Store Features
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Self Pickup
                        </Typography>
                        <Chip 
                          label={selectedStore.selfPickup ? 'Available' : 'Not Available'}
                          color={selectedStore.selfPickup ? 'success' : 'default'}
                          size="medium"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Cash on Delivery
                        </Typography>
                        <Chip 
                          label={selectedStore.restrictCOD ? 'Restricted' : 'Available'}
                          color={selectedStore.restrictCOD ? 'error' : 'success'}
                          size="medium"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                          Store Type
                        </Typography>
                        <Chip 
                          label={selectedStore.isDarkStore ? 'Dark Store' : 'Regular Store'}
                          color={getStoreTypeColor(selectedStore.isDarkStore)}
                          size="medium"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Timeline */}
              {(selectedStore.createdAt || selectedStore.updatedAt) && (
                <Card elevation={1}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Schedule color="primary" />
                      Timeline
                    </Typography>
                    <Grid container spacing={2}>
                      {selectedStore.createdAt && (
                        <Grid item xs={12} md={6}>
                          <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                              Created At
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                              {new Date(selectedStore.createdAt).toLocaleString()}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {selectedStore.updatedAt && (
                        <Grid item xs={12} md={6}>
                          <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                              Last Updated
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                              {new Date(selectedStore.updatedAt).toLocaleString()}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseViewModal} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Store Modal */}
      <Dialog 
        open={editModalOpen} 
        onClose={handleCloseEditModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Edit color="primary" />
            <Typography variant="h5">
              Edit Store - {selectedStore?.storeName}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <form onSubmit={handleEditSubmit}>
              <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                {/* Store Basic Information */}
                <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
                  Store Information
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
                  <TextField
                    fullWidth
                    label="Store Name"
                    name="storeName"
                    value={editFormData.storeName}
                    onChange={handleEditInputChange}
                    required
                    variant="outlined"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={editFormData.isDarkStore}
                        onChange={handleEditInputChange}
                        name="isDarkStore"
                        color="primary"
                      />
                    }
                    label="Dark Store"
                  />

                  {editFormData.isDarkStore && (
                    <FormControl fullWidth required>
                      <InputLabel>Main Store</InputLabel>
                      <Select
                        name="mainStoreId"
                        value={editFormData.mainStoreId}
                        label="Main Store"
                        onChange={handleEditInputChange}
                      >
                        {stores.filter(store => !store.isDarkStore).map((store) => (
                          <MenuItem key={store.id} value={store.branchId}>
                            {store.storeName} ({store.branchId})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <TextField
                    fullWidth
                    label="Branch ID"
                    name="branchId"
                    value={editFormData.branchId}
                    onChange={handleEditInputChange}
                    required
                    variant="outlined"
                    helperText="Enter a unique identifier for this store"
                  />
                </Box>

                {/* Location Information */}
                <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
                  Location Details
                </Typography>

                {/* Google Maps Integration */}
                <Box sx={{ mb: 3 }}>
                  <GoogleMapComponent
                    onLocationSelect={handleEditMapClick}
                    selectedLocation={
                      editFormData.location
                        ? { 
                            lat: parseFloat(editFormData.location.split(',')[0]?.trim()),
                            lng: parseFloat(editFormData.location.split(',')[1]?.trim())
                          }
                        : null
                    }
                  />
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
                    <strong>How to use:</strong> 
                    1. Search for a location using the search box above the map, OR
                    2. Click directly on the map to set the store location.
                    The coordinates will be automatically filled in the form fields below.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Location (lat, lng)"
                      name="location"
                      value={editFormData.location}
                      onChange={handleEditInputChange}
                      required
                      placeholder="17.449800, 78.398870"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOn />
                          </InputAdornment>
                        ),
                      }}
                      helperText="Format: latitude, longitude"
                    />
                    <TextField
                      fullWidth
                      label="Location Code"
                      name="locCode"
                      value={editFormData.locCode}
                      onChange={handleEditInputChange}
                      required
                      placeholder="Z5LYI2KS4C"
                      helperText="Unique location identifier"
                    />
                  </Box>

                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={editFormData.address}
                    onChange={handleEditInputChange}
                    required
                    multiline
                    rows={3}
                    variant="outlined"
                  />

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Pincode"
                      name="pincode"
                      value={editFormData.pincode}
                      onChange={handleEditInputChange}
                      required
                      inputProps={{ maxLength: 6, pattern: '[0-9]{6}' }}
                      helperText="6-digit pincode"
                    />
                    <TextField
                      fullWidth
                      label="Contact Number"
                      name="contactNo"
                      value={editFormData.contactNo}
                      onChange={handleEditInputChange}
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone />
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{ maxLength: 10, pattern: '[6-9][0-9]{9}' }}
                      helperText="10-digit Indian mobile number"
                    />
                  </Box>
                </Box>

                {/* Store Settings */}
                <Typography variant="h6" sx={{ mb: 3, color: 'primary.main' }}>
                  Store Settings
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 4 }}>
                  <TextField
                    fullWidth
                    label="Delivery Radius (meters)"
                    name="deliveryRadius"
                    value={editFormData.deliveryRadius}
                    onChange={handleEditInputChange}
                    required
                    type="number"
                    inputProps={{ min: 100, max: 50000 }}
                    helperText="Enter delivery radius in meters"
                  />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editFormData.status}
                          onChange={handleEditInputChange}
                          name="status"
                          color="primary"
                        />
                      }
                      label="Active Status"
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editFormData.selfPickup}
                          onChange={handleEditInputChange}
                          name="selfPickup"
                          color="primary"
                        />
                      }
                      label="Enable Self Pickup"
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editFormData.restrictCOD}
                          onChange={handleEditInputChange}
                          name="restrictCOD"
                          color="primary"
                        />
                      }
                      label="Restrict Cash on Delivery"
                    />
                  </Box>
                </Box>
              </Box>
            </form>
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseEditModal} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleEditSubmit} variant="contained">
            Update Store
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoreList; 